/**
 * Registrar parecer — conclusão da análise de um caso da régua (Malha),
 * dentro da tarefa. Três caminhos:
 *  - Gasto justificado: fundamento + padrão esperado (proposto do histórico) +
 *    @gestor que aprova (vira subtarefa de aprovação; o tempo de espera é medido).
 *  - Indício de irregularidade: fundamento + evidência; o dossiê entra no card
 *    e em seguida o operador transfere para o setor que escolher.
 *  - Inconclusivo: fundamento; a tarefa é finalizada e o caso volta no mês seguinte.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, HelpCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { malhaAPI } from '../services/api';
import DataBR from './DataBR';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MESES = ['', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesLabel = (m) => `${MESES[Number(m.slice(5, 7))]}/${m.slice(2, 4)}`;

const OPCOES = [
  { v: 'justificado', rot: 'Gasto justificado', icone: ShieldCheck, cor: '#1e8449', desc: 'O gasto tem explicação. Define o padrão esperado e vai para aprovação do gestor.' },
  { v: 'irregular', rot: 'Indício de irregularidade', icone: ShieldAlert, cor: '#c0392b', desc: 'Algo não fecha. Registra o dossiê e transfere para o setor que vai tratar.' },
  { v: 'inconclusivo', rot: 'Inconclusivo', icone: HelpCircle, cor: '#7f8c8d', desc: 'Falta informação para concluir. Finaliza; o caso volta no mês seguinte.' },
];

export default function ParecerModal({ cardId, aberto, onClose, onConcluido, onTransferir }) {
  const [parecer, setParecer] = useState('');
  const [motivo, setMotivo] = useState('');
  const [evidencia, setEvidencia] = useState('');
  const [prop, setProp] = useState(null);
  const [padrao, setPadrao] = useState(null);
  const [aprovadores, setAprovadores] = useState([]);
  const [aprovador, setAprovador] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setParecer(''); setMotivo(''); setEvidencia(''); setAprovador(''); setProp(null); setPadrao(null);
    malhaAPI.aprovadores().then((r) => setAprovadores(r.data || [])).catch(() => {});
  }, [aberto]);

  useEffect(() => {
    if (parecer !== 'justificado' || prop) return;
    malhaAPI.propostaParecer(cardId)
      .then((r) => { setProp(r.data); setPadrao({ faixa_min: r.data.faixa_min, faixa_max: r.data.faixa_max, valido_ate: r.data.valido_ate, composicao: new Set(r.data.composicao_esperada) }); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Falha ao calcular o padrão esperado'));
  }, [parecer, cardId, prop]);

  if (!aberto) return null;

  const salvar = async () => {
    if (!parecer) return toast.error('Escolha o parecer');
    if (motivo.trim().length < 10) return toast.error('Fundamente o parecer (mínimo 10 caracteres)');
    const dados = { parecer, motivo: motivo.trim(), evidencia: evidencia.trim() || null };
    if (parecer === 'justificado') {
      if (!aprovador) return toast.error('Cite o gestor que vai aprovar');
      if (!padrao?.valido_ate) return toast.error('Informe a validade (dd/mm/aaaa)');
      dados.aprovador_id = Number(aprovador);
      dados.padrao = { faixa_min: Number(padrao.faixa_min), faixa_max: Number(padrao.faixa_max), valido_ate: padrao.valido_ate,
        composicao_esperada: [...padrao.composicao], nome: prop?.nome };
    }
    setSalvando(true);
    try {
      const r = await malhaAPI.registrarParecer(cardId, dados);
      if (parecer === 'justificado') toast.success(`Parecer registrado. Aguardando aprovação de ${r.data.aguardando}.`);
      else if (parecer === 'irregular') toast.success('Parecer e dossiê registrados. Escolha o setor para transferir.');
      else toast.success('Parecer registrado e tarefa finalizada.');
      onClose();
      if (parecer === 'irregular' && onTransferir) onTransferir(`Indício de irregularidade: ${motivo.trim()}`);
      else onConcluido && onConcluido();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar o parecer');
    } finally { setSalvando(false); }
  };

  const alternar = (cd) => setPadrao((p) => { const s = new Set(p.composicao); s.has(cd) ? s.delete(cd) : s.add(cd); return { ...p, composicao: s }; });
  const maxSerie = prop ? Math.max(...prop.serie.map((s) => s.valor), 1) : 1;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h3>Registrar parecer</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
          {OPCOES.map(({ v, rot, icone: Icone, cor, desc }) => (
            <button key={v} type="button" onClick={() => setParecer(v)} style={{
              textAlign: 'left', padding: 10, borderRadius: 10, cursor: 'pointer', background: parecer === v ? `${cor}12` : '#fff',
              border: `2px solid ${parecer === v ? cor : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: cor, fontSize: '0.9rem' }}><Icone size={16} /> {rot}</div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 4 }}>{desc}</div>
            </button>
          ))}
        </div>

        {parecer && (
          <>
            <div className="form-group">
              <label>Fundamento do parecer <span style={{ color: '#c0392b' }}>*</span></label>
              <textarea className="form-control" rows="3" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder={parecer === 'irregular' ? 'O que não fecha: horários sobrepostos, documento divergente, prestador…' : 'O que a análise das contas mostrou'} />
            </div>
            <div className="form-group">
              <label>Evidência (opcional)</label>
              <input className="form-control" value={evidencia} onChange={(e) => setEvidencia(e.target.value)} placeholder="laudo, protocolo, documento anexado ao card…" />
            </div>
          </>
        )}

        {parecer === 'justificado' && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>Padrão esperado (o caso continua acompanhado)</div>
            {!prop || !padrao ? <div style={{ color: '#888', fontSize: '0.85rem' }}><Loader size={14} className="spin" /> Calculando a partir dos últimos 12 meses…</div> : (
              <>
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 56, marginBottom: 8 }}>
                  {prop.serie.map((s) => (
                    <div key={s.mes} title={`${mesLabel(s.mes)}: R$ ${fmt(s.valor)}`} style={{ flex: 1, textAlign: 'center', fontSize: '0.6rem', color: '#888' }}>
                      <div style={{ height: Math.max(2, Math.round((s.valor / maxSerie) * 40)), background: s.valor ? 'var(--brand, #20a490)' : '#eee', borderRadius: 3 }} />
                      {mesLabel(s.mes)}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  <div className="form-group"><label>Mínimo por mês (R$)</label><input className="form-control" type="number" value={padrao.faixa_min} onChange={(e) => setPadrao({ ...padrao, faixa_min: e.target.value })} /></div>
                  <div className="form-group"><label>Máximo por mês (R$)</label><input className="form-control" type="number" value={padrao.faixa_max} onChange={(e) => setPadrao({ ...padrao, faixa_max: e.target.value })} /></div>
                  <div className="form-group"><label>Válido até</label><DataBR value={padrao.valido_ate} onChange={(iso) => setPadrao({ ...padrao, valido_ate: iso })} /></div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: -6, marginBottom: 8 }}>Proposta: {prop.meses_com_uso} mês(es) com uso, mínimo e máximo ± {prop.tolerancia_pct}%. Acima do máximo ou grupo fora da lista = volta a abrir tarefa.</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {prop.grupos.map((g) => (
                    <label key={g.cd_grupo} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', background: padrao.composicao.has(g.cd_grupo) ? '#e8f6f4' : '#fff' }}>
                      <input type="checkbox" checked={padrao.composicao.has(g.cd_grupo)} onChange={() => alternar(g.cd_grupo)} />
                      {g.ds_grupo} <span style={{ color: '#888' }}>· {g.meses} mês(es)</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>@ Gestor que aprova <span style={{ color: '#c0392b' }}>*</span></label>
              <select className="form-control" value={aprovador} onChange={(e) => setAprovador(e.target.value)}>
                <option value="">Selecione…</option>
                {aprovadores.map((u) => <option key={u.id} value={u.id}>@{u.nome} ({u.email})</option>)}
              </select>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>Ele recebe um alerta no sino. O tempo até a decisão fica registrado na subtarefa.</div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button className="btn btn-success" onClick={salvar} disabled={salvando || !parecer}>{salvando ? 'Registrando…' : 'Registrar parecer'}</button>
        </div>
      </div>
    </div>
  );
}
